import { BOARD } from './board'
import type {
  ActiveEffect,
  EffectKind,
  GameState,
  PaydayDetail,
  Player,
  Profession,
} from './types'

/** รวมเอฟเฟกต์ของผู้เล่น + เอฟเฟกต์ระดับโลก */
export function allEffects(state: GameState, p: Player): ActiveEffect[] {
  return [...p.effects, ...state.globalEffects]
}

export function hasEffect(state: GameState, p: Player, kind: EffectKind): boolean {
  return allEffects(state, p).some((e) => e.kind === kind)
}

/** ตัวคูณรวม (คูณกันทุกใบที่ active) — ใช้กับ salaryMult / rentEarnMult / passiveMult ฯลฯ */
export function mult(state: GameState, p: Player, kind: EffectKind): number {
  return allEffects(state, p).reduce((acc, e) => (e.kind === kind ? acc * e.magnitude : acc), 1)
}

/** ค่าที่มากที่สุดของเอฟเฟกต์ชนิดนั้น (ใช้กับ debtSurge / extraDie) */
export function effectSum(state: GameState, p: Player, kind: EffectKind): number {
  return allEffects(state, p).reduce((acc, e) => (e.kind === kind ? acc + e.magnitude : acc), 0)
}

export function diceCap(state: GameState, p: Player): number {
  return allEffects(state, p).reduce(
    (acc, e) => (e.kind === 'diceCap' ? Math.min(acc, e.magnitude) : acc),
    6,
  )
}

/** ---------- งบรายจ่าย ---------- */

/** รายจ่ายประจำตามอาชีพ (ยังไม่รวมค่าเลี้ยงดูบุตรและค่าผ่อนสินเชื่อ) */
export function professionExpenses(prof: Profession): number {
  const e = prof.expenses
  // ครูโรบอท (perk frugal) ไม่มีหนี้บัตรเครดิต
  const card = prof.perkId === 'frugal' ? 0 : e.card
  return e.tax + e.home + e.car + card + e.other
}

export function baseExpenses(p: Player): number {
  return professionExpenses(p.profession)
}

export function childExpenses(p: Player): number {
  return p.children * p.profession.childCost
}

export function loanPayments(p: Player): number {
  return p.liabilities.reduce((s, l) => s + l.monthly, 0)
}

export function totalExpenses(p: Player): number {
  return baseExpenses(p) + childExpenses(p) + loanPayments(p)
}

/** ---------- Passive Income ---------- */

/** กระแสเงินสดต่อเดือนจากโฉนดบนกระดาน (รวมทุกระดับอัปเกรด) */
export function propertyIncome(state: GameState, p: Player): number {
  let sum = 0
  state.tiles.forEach((ts, id) => {
    if (ts.ownerId !== p.id || ts.mortgaged) return
    const tile = BOARD[id]
    if (!tile.monthly) return
    if (tile.kind === 'utility' && hasEffect(state, p, 'utilityOff')) return
    sum += tile.monthly[Math.min(ts.level, tile.monthly.length - 1)]
  })
  return sum
}

/** กระแสเงินสดจากสินทรัพย์การ์ด DEAL */
export function assetIncome(p: Player): number {
  return p.assets.reduce((s, a) => s + a.cashflow, 0)
}

export function passiveIncome(state: GameState, p: Player): number {
  const raw = assetIncome(p) + propertyIncome(state, p)
  return Math.round(raw * mult(state, p, 'passiveMult'))
}

/** ---------- Payday ---------- */

export function paydayDetail(state: GameState, p: Player): PaydayDetail {
  const salary = Math.round(p.profession.salary * mult(state, p, 'salaryMult'))
  const passive = passiveIncome(state, p)
  const expenses = baseExpenses(p) + childExpenses(p)
  const surge = effectSum(state, p, 'debtSurge')
  const debtBalance = p.liabilities.reduce((s, l) => s + l.balance, 0)
  const loans = loanPayments(p) + Math.round(debtBalance * surge)
  const net = Math.round((salary + passive - expenses - loans) * mult(state, p, 'paydayMult'))
  return { salary, passive, expenses, loans, net }
}

/** ---------- มูลค่าและเงื่อนไขชนะ ---------- */

export function propertyValue(state: GameState, p: Player): number {
  let sum = 0
  state.tiles.forEach((ts, id) => {
    if (ts.ownerId !== p.id) return
    const tile = BOARD[id]
    sum += (tile.price ?? 0) + ts.level * (tile.upgradeCost ?? 0)
  })
  return sum
}

export function assetValue(p: Player): number {
  return p.assets.reduce((s, a) => s + a.value, 0)
}

/** สินทรัพย์รวม (ใช้คิดภาษี) — ไม่รวมเงินสด */
export function totalAssets(state: GameState, p: Player): number {
  return propertyValue(state, p) + assetValue(p)
}

export function netWorth(state: GameState, p: Player): number {
  const debt = p.liabilities.reduce((s, l) => s + l.balance, 0)
  return p.cash + totalAssets(state, p) - debt
}

/** เป้าหมายที่ต้องทำให้ได้เพื่อหลุดวงจรหนู */
export function freedomTarget(state: GameState, p: Player): number {
  return Math.round(totalExpenses(p) * state.settings.freedomMultiplier)
}

export function isFinanciallyFree(state: GameState, p: Player): boolean {
  return passiveIncome(state, p) >= freedomTarget(state, p) && freedomTarget(state, p) > 0
}

/* ---------- มูลค่าเวลาขาย/จำนอง ---------- */

/** ขายโฉนดคืนธนาคารได้ครึ่งเดียวของมูลค่าที่ลงไป */
export const PROPERTY_SELL_RATE = 0.5
/** ขายสินทรัพย์จากการ์ด DEAL โดนหักค่าธรรมเนียม 15% */
export const SELL_HAIRCUT = 0.85
export const MORTGAGE_RATE = 0.5
export const UNMORTGAGE_RATE = 0.55

const round1k = (n: number) => Math.round(n / 1000) * 1000

export function mortgageAmount(tileId: number): number {
  return round1k((BOARD[tileId].price ?? 0) * MORTGAGE_RATE)
}

export function unmortgageCost(tileId: number): number {
  return round1k((BOARD[tileId].price ?? 0) * UNMORTGAGE_RATE)
}

/** หนี้ที่ผูกกับโฉนดช่องนี้ */
export function tileLoan(p: Player, tileId: number): number {
  return p.liabilities.find((l) => l.tileId === tileId)?.balance ?? 0
}

/** หนี้ที่ผูกกับสินทรัพย์ใบนี้ */
export function assetLien(p: Player, assetName: string): number {
  return p.liabilities.find((l) => l.name === `สินเชื่อ ${assetName}`)?.balance ?? 0
}

/** เงินที่จะได้จริงหลังหักหนี้ ถ้าขายโฉนดช่องนี้ตอนนี้ (ติดลบได้ถ้าหนี้ท่วม) */
export function propertySaleNet(state: GameState, p: Player, tileId: number): number {
  const ts = state.tiles[tileId]
  const tile = BOARD[tileId]
  const gross = round1k(((tile.price ?? 0) + ts.level * (tile.upgradeCost ?? 0)) * PROPERTY_SELL_RATE)
  const loan = tileLoan(p, tileId)
  // จำนองไปแล้วเท่ากับรับเงินล่วงหน้าไปครึ่งหนึ่ง ตอนขายจึงต้องคืนส่วนนั้น
  const advanced = ts.mortgaged ? Math.round((tile.price ?? 0) * MORTGAGE_RATE) : 0
  return gross - advanced - loan
}

/** เงินที่จะได้จริงหลังหักหนี้ ถ้าขายสินทรัพย์ใบนี้ตอนนี้ */
export function assetSaleNet(p: Player, assetUid: string): number {
  const a = p.assets.find((x) => x.uid === assetUid)
  if (!a) return 0
  return Math.round((a.value * SELL_HAIRCUT) / 100) * 100 - assetLien(p, a.name)
}

/**
 * เงินสดสูงสุดที่ระดมได้ถ้าขายทุกอย่าง — ใช้ตัดสินว่าล้มละลายหรือยัง
 *
 * ต้องหักหนี้ที่ผูกกับทรัพย์แต่ละชิ้นด้วย ไม่งั้นระบบจะบอกว่า "ยังรอด"
 * ทั้งที่ขายหมดแล้วเงินยังติดลบ แล้วผู้เล่นจะค้างอยู่กับหน้าต่างระดมทุน
 * โดยไม่มีอะไรให้ขายอีก (ทางตัน) — ชิ้นที่ขายแล้วขาดทุนจะไม่ถูกนับ
 * เพราะผู้เล่นย่อมไม่เลือกขาย
 */
export function liquidationValue(state: GameState, p: Player): number {
  let sum = p.cash
  state.tiles.forEach((ts, id) => {
    if (ts.ownerId !== p.id) return
    // โฉนดที่ผ่อนอยู่ ขายแล้วมักติดลบเพราะหนี้ท่วมราคาขาย แต่ "จำนอง" ยังได้เงินสด
    // 50% โดยไม่ต้องปิดหนี้ก่อน — ทางรอดนี้ต้องนับด้วย ไม่งั้นคนที่ใช้เลเวอเรจ
    // จะถูกตัดสินว่าล้มละลายทั้งที่ยังแก้เกมได้
    const bySelling = propertySaleNet(state, p, id)
    const byMortgaging = ts.mortgaged ? 0 : mortgageAmount(id)
    sum += Math.max(0, bySelling, byMortgaging)
  })
  sum += p.assets.reduce((s, a) => s + Math.max(0, assetSaleNet(p, a.uid)), 0)
  return sum
}

/** มีอะไรให้ขายเพื่อกู้สถานการณ์อีกไหม */
export function hasSomethingToSell(state: GameState, p: Player): boolean {
  if (p.assets.length > 0) return true
  return state.tiles.some((ts) => ts.ownerId === p.id)
}
